import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useLanguage } from "@/contexts/LanguageContext.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";

function LegalAccordion({ sections = [] }) {
  const [openIndex, setOpenIndex] = React.useState(0);

  return (
    <div className="space-y-3">
      {sections.map((section, index) => {
        const isOpen = openIndex === index;
        return (
          <SettingsCard
            key={section.title}
            className="overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenIndex((prev) => (prev === index ? -1 : index))}
              className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left md:px-5"
              aria-expanded={isOpen}
            >
              <span className="text-title-medium text-on-surface">{section.title}</span>
              <span className="shrink-0 text-on-surface-var">
                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </span>
            </button>

            <div
              className={[
                "grid transition-all duration-300 ease-out",
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-70",
              ].join(" ")}
            >
              <div className="overflow-hidden">
                <div className="border-t border-[rgba(var(--clr-outline-variant),0.6)] px-4 pb-4 md:px-5 md:pb-5">
                  <div className="space-y-3 pt-3">
                    {section.body.map((paragraph) => (
                      <p
                        key={`${section.title}-${paragraph.slice(0, 28)}`}
                        className="text-body-medium text-on-surface-var"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </SettingsCard>
        );
      })}
    </div>
  );
}

function LegalNavLink({ to, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center rounded-full bg-[rgba(var(--clr-on-surface),0.06)] px-3 py-1 text-label-medium text-primary no-underline"
    >
      {label}
    </Link>
  );
}

export default function LegalDocumentPage({
  title,
  subtitle,
  effectiveDate,
  sections = [],
  siblingLink,
  extraLinks = [],
}) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const allLinks = [
    { to: "/login", label: t("auth.login.password.back", "Back to welcome") },
    siblingLink,
    ...extraLinks,
  ].filter((link) => link?.to && link?.label);
  const content = (
    <SettingsScreenShell>
      <TopBarCenter
        title={title}
        onBack={() => navigate(-1)}
      />

      <div className="space-y-4">
        <div className="space-y-2">
          <div className="text-label-large text-on-surface-var">
            {t("legal.common.category", "Legal")}
          </div>
          <p className="text-body-medium text-on-surface-var">{subtitle}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-[rgba(29,79,215,0.08)] px-3 py-1 text-label-medium text-primary">
            {t("legal.common.effectiveLabel", "Effective")}: {effectiveDate}
          </span>
          {allLinks.map((link) => (
            <LegalNavLink key={`${link.to}-${link.label}`} to={link.to} label={link.label} />
          ))}
        </div>

        {sections.length ? (
          <LegalAccordion sections={sections} />
        ) : (
          <SettingsCard className="p-4 text-body-medium text-on-surface-var">
            {t(
              "legal.common.empty",
              "Legal content is not available right now. Please try again shortly.",
            )}
          </SettingsCard>
        )}
      </div>
    </SettingsScreenShell>
  );

  return <div className="min-h-dvh bg-[rgb(var(--clr-background))]">{content}</div>;
}
